
/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { merge as observableMerge, BehaviorSubject, Observable } from 'rxjs';

import { map } from 'rxjs/operators';
import { DataSource } from '@angular/cdk/table';
import { FileItem } from 'ng2-file-upload';
import { remove as _remove } from 'lodash';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';

export class TableDatabase<T> {
  public dataChange: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
  public set data(data: T[]) { this.dataChange.next(data); }
  public get data(): T[] { return this.dataChange.getValue(); }

  /**
   * Add New array of single items to the database
   * @param  {T[]}    items
   */
  public add(items: T[], reset: boolean = false) {
    const copiedData = (reset) ? [] : this.data.slice();
    items.forEach(item => copiedData.push(item));
    this.dataChange.next(copiedData);
  }

  /**
   * Remove single item from dastabase
   * @param  {T}      item [description]
   * @return {[type]}      [description]
   */
  public remove(item: T) {
    this.dataChange.next(_remove(this.data, (itm: T) => { return itm !== item; }));
  }

  /**
   * Wire up the Observer
   * @return {Observable<T[]>} [description]
   */
  connect(): Observable<T[]> {
    return this.dataChange;
  }

  disconnect() { }
}

export class TableDatabaseAdvanced<T> extends DataSource<T> {
  public dataChange: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
  public set data(data: T[]) { this.dataChange.next(data); }
  public get data(): T[] { return this.dataChange.value; }

  constructor(private primarySource: any, private _paginator: MatPaginator, private _sort?: MatSort) {
    super();
    this.add(primarySource);
    _paginator.length = primarySource.length;
  }

  public count() {
    return this.data.length;
  }
  /**
   * Add New array of single items to the database
   * @param  {T[]}    items
   */
  public add(items: T[]) {
    const copiedData = this.data.slice();
    items.forEach(item => copiedData.push(item));
    this.dataChange.next(copiedData);
  }

  /**
   * Remove single item from database
   * @param  {T}      item [description]
   * @return {[type]}      [description]
   */
  public remove(item: T) {
    this.dataChange.next(_remove(this.data, (itm: T) => { return itm !== item; }));
  }

  /**
   * Wire up the Observer
   * @return {Observable<T[]>} [description]
   */
  connect(): Observable<T[]> {

    const displayDataChanges = [
      this.dataChange,
      this._paginator.page,
      this._sort && this._sort.sortChange,
    ];

    return observableMerge(...displayDataChanges).pipe(map(() => {
      const tmp = this.getSortedData();

      // Grab the page's slice of data.
      const startIndex = this._paginator.pageIndex * this._paginator.pageSize;
      return tmp.splice(startIndex, this._paginator.pageSize);
    }));
  }

  /**
   * Returns a sorted copy of the database data.
   * @return {T[]} [description]
   */
  getSortedData(): T[] {
    const data = this.data.slice();
    if (!this._sort || !this._sort.active || this._sort.direction === '') { return data; }

    return data.sort((a, b) => {
      const propertyA: number | string = '';
      const propertyB: number | string = '';

      const valueA = isNaN(+propertyA) ? propertyA : +propertyA;
      const valueB = isNaN(+propertyB) ? propertyB : +propertyB;

      return (valueA < valueB ? -1 : 1) * (this._sort.direction === 'asc' ? 1 : -1);
    });
  }

  /**
   * Observer Disconnect
   * @return {[type]} [description]
   */
  disconnect() { }
}

export class BaseMongoRecordObject {
  public _id: string;
}

interface AuthenticationProvider {
  type: 'FEDERATION' | 'OKTA' | string;
  name: string;
}

interface InitiativePermission {
  initiativeId: string;
  permissions: string[];
}

export class User extends BaseMongoRecordObject {
  public title: string;
  public firstName: string | undefined;
  public surname: string | undefined;
  public email: string;
  public password: string;
  public telephoneNumber: string;
  public mobileNumber: string;
  public profile: string;
  public partnerTypes: string[];
  public stakeholderTypes: string[];
  public dashboardPermissions: string[] = [];
  public initiativePermissions: string[] = [];
  public organizationId: string;
  public jobTitle: string;
  public active: boolean;
  public isStaff: boolean;
  public isSuperUser: boolean = false;
  public permissions: InitiativePermission[] = [];
  public loginAttempts: number;
  public oktaUserId: string | undefined;
  public authenticationProvider?: AuthenticationProvider;
  public created: string;
  public lastLogin: string;
  public metadata: object;
}

export class Address {
  public line1: string;
  public line2: string;
  public city: string;
  public postcode: string;
  public countryCode: string;
}

export class Organization extends BaseMongoRecordObject {
  public code: string;
  public name: string;
  public profile: string;
  public address: Address = new Address();
  public organizationType: string;
  public partnerTypes: string;
  public parentId: string;
  public extraFields: Array<object>;
}

export class IndustryLevels {

  level1: string = undefined;
  level2: string = undefined;
  level3: string = undefined;
  level4: string = undefined;

  static withEmptyValues() {
    const levels = new IndustryLevels();
    levels.level1 = '';
    levels.level2 = '';
    levels.level3 = '';
    levels.level4 = '';
    return levels;
  }
}

export class Industry {
  gics: IndustryLevels;
  icb: IndustryLevels;
  icb2019: IndustryLevels;

  static withEmptyValues() {
    const industry = new Industry();
    industry.gics = IndustryLevels.withEmptyValues();
    industry.icb = IndustryLevels.withEmptyValues();
    industry.icb2019 = IndustryLevels.withEmptyValues();

    return industry;
  }
}

export const codeRegex = /^[a-z0-9\/\-.]+$/;
export const codeRegexErrorMsg = 'Code can only contain alphanumeric, dash(-), dot(.) and forward slash(/)';

export const defaultUsage = [
  'menu_dropdowns',
  'initiative_structure',
  'menu_summary',
];

class InitiativeRatings {
  code: string;
  rating: string;
  link?: string;
  linkText?: string;
  date?: string;
}

export class InitiativeMetadata {
  sgx_ric?: string;
  sgx_code?: string;
  sgx_issuer_name?: string;

  static withEmptyValues() {
    const meta = new InitiativeMetadata();
    meta.sgx_issuer_name = '';
    meta.sgx_code = '';
    meta.sgx_ric = '';
    return meta;
  }
}

class SubscriptionItem {
  id: string;
  priceId: string;
  productId: string;
  productCode?: string;
  created: number;
}

type SubscriptionStatus =
  | 'active'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'past_due'
  | 'trialing'
  | 'unpaid'

export const requireNewSubStatus: SubscriptionStatus[] = [
  'canceled',
  'incomplete_expired',
];

export class Subscription {
  id: string;
  items: SubscriptionItem[];
  status: SubscriptionStatus;
  periodStart: number;
  periodEnd: number;
  trialStart?: number;
  trialEnd?: number;
  paymentProvider: 'stripe' | 'wwg';
}

class Customer {
  id: string;
  subscriptions: Subscription[];
}

export class Initiative extends BaseMongoRecordObject {
  public code: string;
  public name: string;
  public type: string = 'initiative'; // enum initiative | initiativeGroup
  public initiativeGroupId: string;
  public description: string;
  public missionStatement: string;
  public created: Date = new Date();
  public startDate: Date = new Date();
  public endDate: Date = new Date();
  public geoLocation: string;
  public files: any[];
  public profile: string;
  public sdgImpact: Array<string>;
  public visible: boolean = true;
  public extraFields: Array<object>;
  public industry?: Industry;
  public parentId: string;
  public permissionGroup: string = 'free'; // enum free | premium_limit5 | premium_trial_limit1 | premium_unlimited | etc etc
  public organizationId: string;
  public tags: (string | InitiativeTags)[];
  public usage: string[] = defaultUsage;
  public linkedUniversalTrackers: Array<LinkedUniversalTracker>;
  public ratings: InitiativeRatings[];
  public createInitiativeGroup: boolean = false;
  public customer?: Customer;
  public oktaGroupIds: string[] = [];
  public metadata?: InitiativeMetadata = {};
  public appConfigCode: string | undefined;
}

export class InitiativeGroupInitiative {
  public initiativeId: string;
  public weight: number = 0;
}

export class InitiativeGroup extends BaseMongoRecordObject {
  public code: string;
  public name: string;
  public group: Array<InitiativeGroupInitiative> = [];
}

export class LinkedUniversalTracker {
  public universalTrackerId: string;
  public usage: Array<string> = [];
}

export class ImpactLevel extends BaseMongoRecordObject {
  public code: string;
  public name: string;
  public parentId: string;
}

export class DisaggregationTypeValue {
  public code: string;
  public name: string;
}

export class DisaggregationType extends BaseMongoRecordObject {
  public code: string;
  public values: DisaggregationTypeValue[];
}

export class Option {
  public code: string;
  public name: string;
}

export class ValueList extends BaseMongoRecordObject {
  public code: string;
  public name: string;
  public options: Option[];
}

export interface Issuer {
  name: string;
  _id: string;
}

export enum ColumnType {
  Number = 'number',
  Text = 'text',
}

export enum ValueAggregation {
  ValueSumAggregator = 'valueSumAggregator',
  ValueCountAggregator = 'valueCountAggregator',   // Just count utrvs
  ValueAverageAggregator = 'valueAverageAggregator',
  ValueListCountAggregator = 'valueListCountAggregator',
  TextCountAggregator = 'textCountAggregator',
  NumericValueListSumAggregator = 'numericValueListSumAggregator',
  TableColumnAggregator = 'tableAggregator',
  TableConcatenationAggregator = 'tableConcatenationAggregator', // Just appends row as new rows
  LatestAggregator = 'latestAggregator',
  EmptyAggregator = 'emptyAggregator',
}

export interface ConfigurationVariableSetup {
  code: string
  valueListCode?: string;
  valueAggregation?: ValueAggregation;
}

export interface Calculation {
  variables: Record<string, ConfigurationVariableSetup>;
  formula: string;
  useDisaggregations?: boolean;
}

export interface Connection extends Calculation {
  score: number; // 0-1 (float)
}


export interface TableColumn {
  type: ColumnType | string;
  code: string;
  name: string;
  shortName?: string;
  options?: Option[];
  unit?: string;
  unitType?: string;
  numberScale?: string;
  instructions?: string;
  listId?: string;
  calculation?: {
    formula?: string;
  };
  visibilityRule?: {
    formula?: string;
  };
  validation?: {
    max?: number;
    min?: number;
    required?: boolean;
    allowCustomOptions?: boolean;
  };
}

export interface ValueTable {
  validation?: {
    maxRows: number,
  };
  columns: TableColumn[];
}

export class CompanyDataField extends BaseMongoRecordObject {
  public code: string;
  public name: string;
}

export class CompanyData extends BaseMongoRecordObject {
  public organizationId: string;
  public effectiveDate: string;
  public fields: any;
}

export class ReportingFrameworkItem {
  public itemCode: string;
  public name: string;
  public description: string;
  public sdgCodes: string[];
}

export class ReportingFramework extends BaseMongoRecordObject {
  public code: string;
  public name: string;
  public shortName: string;
  public description: string;
  public type: string;
  public items: ReportingFrameworkItem[];
}

export class MetricUnit extends BaseMongoRecordObject {
  public code: string;
  public name: string;
  public prefix: string;
  public suffix: string;
}

export class ReferenceData extends BaseMongoRecordObject {
  public initiativeId: string;
  public effectiveDate: Date;
  public metricUnitMap: any;
}

export interface ValueOption {
  code: string;
  name: string;
}

export interface ValidationOptions {
  type: 'list' | 'custom';
  custom?: ValueOption[] | any;
  listId?: string;
  allowCustomOptions?: Boolean;
}

export interface ValueValidation {
  min?: number;
  max?: number;
  valueList?: ValidationOptions;
  table?: ValueTable;
}

export class Tags {
  tcfd?: Array<string> = [];
  pic?: Array<string> = [];
  ese?: Array<string> = [];
  sdg?: Array<string> = [];
}

export class UniversalTrackerTextLabels {
  public name: string = '';
  public description?: string;
  public valueLabel: string = '';
  public sampleSizeLabel?: string;
  public instructions?: string;
  public instructionsLink?: string;
  public typeCode: string;
  public typeTags?: string[] | string;
  public evidenceInstructions?: string;
  public alternativeType: 'language';
  public languageCode: string;
}

export class Alternatives {
  [key: string]: UniversalTrackerTextLabels;
}

export class UniversalTracker extends BaseMongoRecordObject {
  public code: string;
  public name: string;
  public description: string;
  public valueLabel: string;
  public sampleSizeLabel: string;
  public instructions: string;
  public tags?: Tags = new Tags();
  public alternatives?: Alternatives;
  public evidenceInstructions: string;
  public type: string;
  public typeCode: string;
  public targetDirection: string = 'tracker'; // enum: ['tracker', 'increase','decrease']
  public valueType: string = 'number';
  public valueAggregation: string;
  public valueListOrdered: string[];
  public valueListTargets: string[];
  public metricUnitCode: string;
  public profile: string;
  public unitType: string;
  public ownerId: string;
  public unit: string;
  public numberScale: string = '';
  public numberScaleInput: string = '';
  public connections: Connection[] | undefined;
  public valueValidation: ValueValidation;
}

export class UniversalTrackerValue extends BaseMongoRecordObject {
  public universalTrackerId: string;
  public initiativeId: string;
  public impactLevelId: string;
  public code: string;
  public sourceType: string = 'manual'; // enum: ['survey', 'manual', 'composite', 'fragment']
  public sourceCode: string;
  public lastUpdateDate: Date;
  public effectiveDate: Date;
  public value: number;
  public instructions: string;
  public sampleSize: number;
  public verificationRequired: boolean = true;
  public evidenceRequired: boolean;
  // public evidence: string; // TODO
  public type: string; // enum: ['actual', 'baseline', 'target']
  public status: string; // enum: ['created', 'updated', 'verified', 'rejected'],
  public stakeholders: StakeholderGroup = new StakeholderGroup();
  public escalationPolicy?: EscalationPolicy;
  public history: UniversalTrackerValueHistory[];
  public disaggregationSummary: UniversalTrackerValueDisaggregationSummary;
  public disaggregationTypes: string[];
  public disaggregations: UniversalTrackerValueDisaggregation[];
}

export class UniversalTrackerValueWithTracker extends UniversalTrackerValue {
  public universalTrackerData: string;
}

export class UniversalTrackerValueDisaggregationSummary {
}

export class UniversalTrackerValueHistory {
  public action: string; // enum['created', 'updated', 'verified', 'rejected']
  public userId: string;
  public date: Date;
  public value: number;
  public sampleSize: number;
  public evidence: string;
}

export class UniversalTrackerValueDisaggregation {
  public name: string;
  public value: string;
  public count: number; // Often in surveys a value may apply to multiple individuals, who are summed up here.
  public dateCollected: Date;
  public sourceReference: string;
}

export class UniversalTrackerGroup extends BaseMongoRecordObject {
  public initiativeId: string;
  public code: string;
  public name: string;
  public universalTrackers: string[];
}

interface History {
  created: Date;
  universalTrackerValueId: string;
}

export class UniversalTrackerSchedule extends BaseMongoRecordObject {
  public code: string;
  public name: string;
  public universalTrackerId: string;
  public initiativeId: string;
  public type: string; // enum: ['actual', 'baseline', 'target']
  public instructions: string;
  public evidenceRequired: boolean = false;
  public verificationRequired: boolean = true;
  public stakeholders: StakeholderGroup = new StakeholderGroup();
  public escalationPolicy?: EscalationPolicy;
  public startDate: Date = new Date();
  public nextRunDate?: string;
  public endDate: Date;
  public history: History[] = [];
  public cronSchedule: string = '0 7 1 * *';
  public enabled: boolean = true;
}

export class UniversalTrackerScheduleExtended extends UniversalTrackerSchedule {
  public universalTrackerValues: any[];
}

export enum EscalationPresetType {
  Default = 'default',
  Custom = 'custom',
  Preset = 'preset',
}

interface EscalationHistory {
  action: string;
  date: Date;
}

export class EscalationPolicy {
  public type: string = EscalationPresetType.Default;
  public isEscalated: boolean = false;
  public history: EscalationHistory[];
  public triggerDate?: Date;
  public presetId?: string;
  public custom?: any;
}

export class StakeholderGroup {
  public stakeholder: string[] = [];
  public verifier: string[] = [];
  public escalation: string[] = [];
}

export class CSVSurveyData extends BaseMongoRecordObject {
  public name: string;
  public description: string;
  public effectiveDate: Date;
  public data: Object[][];
  public dataType: string;
  public startRow: number;
  public disaggregationTypes: string[];
  public universalTrackers: string[];
  public initiativeCodeColumn: string;
  public sourceReferenceColumn: string;
  public initiativeCodeMappingData: any; // blob
  public importConfigurationData: any; // blob
}

export class Framework extends BaseMongoRecordObject {
  public code: string;
  public organizationId: string;
  public initiativeId: string;
  public impactLevelId: string;
  public phases: FrameworkPhase[];
  public universalTrackers: FrameworkUtr[];
}

export class FrameworkPhase extends BaseMongoRecordObject {
  public name: string;
  public stages: FrameworkStage[];
}

export class FrameworkStage extends BaseMongoRecordObject {
  public name: string;
  public startDate: Date;
  public endDate: Date;
}

export class FrameworkKpi extends BaseMongoRecordObject {
  public frameworkId: string;
  public organizationId: string;
  public code: string;
  public name: string;
  public values: Map<string, FrameworkKpiValue>;
}

export class FrameworkKpiValue extends BaseMongoRecordObject {
  public value: boolean = false;
  public created: Date;
  public createdBy: string;
  public verified: Date;
  public verifiedBy: string;
  public rejected: Date;
  public rejectedBy: string;
}

interface CustomGroup {
  name?: string;
  logo?: string;
  objectId?: string;
}

export interface Group {
  objectType: string;
  object: CustomGroup;
  utrs: string[];
}

export class FrameworkPlanner extends BaseMongoRecordObject {
  public code: string;
  public initiativeId: string;
  public phases: FrameworkPhase[];
  public groups: Group[] = [];
  public stakeholders?: StakeholderGroup;
}

export class FrameworkUtr extends BaseMongoRecordObject {
  public organizationId?: string;
  public code: string;
  public name: string;
  public universalTrackerValues: PrivateUtrValue[];
  public stakeholders?: StakeholderGroup;
}

export class PrivateUtrValue {
  public id?: string;
  public value: boolean = false;
  public name: string = '';

  public note: string = '';

  public instructions: string = '';
  public evidenceRequired: boolean = false;
  public verificationRequired: boolean = true;

  public created?: Date | string;
  public createdBy?: string;

  public verified?: Date | string;
  public verifiedBy?: string;

  public rejected?: Date | string;
  public rejectedBy?: string;

  public startDate: Date | string;
  public endDate: Date | string;

  public stakeholders?: StakeholderGroup;
  public escalationPolicy?: EscalationPolicy;
}

export class Survey extends BaseMongoRecordObject {
  public code: string;
  public name: string;
  public sourceName: string;
  public effectiveDate: Date;
  public deletedDate?: Date;
  public utrvType: string; // enum: ['actual', 'baseline', 'target']
  public initiativeId: string;
  public stakeholders: StakeholderGroup = new StakeholderGroup();
  public evidenceRequired: boolean = false;
  public verificationRequired: boolean = true;
  public created: Date;
  public escalationPolicy?: EscalationPolicy;
  public fragmentUtrvs: string[] = [];
}

class InitiativeConfig {
  name: string;
  initiativeCode: string;
  parentInitiativeCode: string;
  initiativeId?: string;
  parentInitiativeId?: string;
  initiativeGroupId?: string;
}

export enum EmailTemplate {
  SurveyContributorVerifierAssurer = 'survey-contributor-verifier-assurer',
  User = 'user',
  Manager = 'manager',
  Guest = 'guest'
}

class OnboardingUser {
  initiativeIds: string[] = [];
  firstName?: string;
  surname?: string;
  email: string;
  dashboardPermissions: string[];
  emailTemplate: EmailTemplate | undefined;
  permissions: InitiativePermission[] = [];
  sfId?: string;
  isAdmin: boolean = false;
}

export enum OnboardingStatus {
  NotStarted = 'not_started',
  Pending = 'pending',
  Started = 'started',
  Rejected = 'rejected',
  Complete = 'complete',
  Deleted = 'deleted',
}

export class Onboarding extends BaseMongoRecordObject {
  token: string;
  status: OnboardingStatus = OnboardingStatus.NotStarted;
  onboardingListId: string;
  user = new OnboardingUser();
  initiativeId: string;
  initiativeConfig = new InitiativeConfig();
  created: Date;
}

export class OnboardingList extends BaseMongoRecordObject {
  code: string;
  name: string;
}

export interface UserEvent {
  id: number;
  email: string;
  userId?: any;
  service: string;
  data: any;
  level: number;
  event: string;
  eventDate: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface InitiativeRating {
  code: string;
  rating: string;
}

// Untouched so far *************************************** //

export class Read {
  public recipient: ReadDetails = new ReadDetails();
  public owner: ReadDetails = new ReadDetails();
}

export class ReadDetails {
  public isRead: boolean = false;
  public date: Date = new Date();
}

export class Reply {
  public id: string;
  public date: Date = new Date();
  public body: string;
  public from: string;
  public email: any;
}

export class Message {
  public id: string;
  public subject: string;
  public type: string;
  public body: string;
  public recipient: string;
  public from: string;
  public to: string;
  public email: any;
  public read: Read = new Read();
  public replies: Reply[] = [];
  public date: Date = new Date();
}

/**
 * File Upload Data
 * @type {File}
 */
export class File {
  public name: string;
  public filename: string;
  public mime: string;
  public data: string;
  public date: Date = new Date();
}

export class EmailTransaction {
  id?: string;
  externalId: string;
  userId: string;
  user?: User;
  service: string;
  type?: string;
  subType?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface EmailSummaryItem {
  type: string;
  count: number;
}

/**
 * Associated Messages Data
 * @type {Notes}
 */
export class Note {
  public note: string;
  public author: string;
}

/**
 * Associated Documents Data
 * @type {AssociatedDocuments}
 */
export class AssociatedDocument {
  public _id: string;
  public title: string;
  public description: string;
  public created: Date;
  public metadata: DocumentMetadata;
  public size: number;
  public userId: string;
  public ownerId: string;
  public ownerType: string;
  public titleChange?: boolean;
  public descChange?: boolean;
  public showSave?: boolean;
  public url?: string;
}

export interface DocumentMetadata {
  name: string;
  mimetype: string;
  extension: string;
}

export interface UploadDocument {
  title: string;
  description: string;
  ownerId: string;
  ownerType: string;
}


export interface OktaGroup {
  id: string;
  profile: {
    name: string;
    description: string | null;
  }
  type: string;
  created: string;
}

export interface ImportSurveyData {
  initiativeCode: string
  effectiveDate: string
  evidenceRequired: boolean
  verificationRequired: boolean
  utrvType: string
  scope?: Record<string, string[]>
}

// Table source handling
export interface DocsData {
  file: FileItem;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message: string | null;
}

export enum InitiativeTags {
  StaffOrganization = 'staff_organization',
  VerifiedOrganization = 'verified_organization',
  Organization = 'organization',
  Country = 'country',
}

export enum ValueChainCategory {
  ProductServices = 'products-services',
  Operations = 'operations',
  ExternalCsr = 'external-csr',
}

export const valueChainCategories: string[] = [
  ValueChainCategory.ProductServices,
  ValueChainCategory.Operations,
  ValueChainCategory.ExternalCsr,
]

// Only one of these should be active
export const singleOptionTags = [
  ValueChainCategory.ProductServices,
  ValueChainCategory.Operations,
  ValueChainCategory.ExternalCsr,
  InitiativeTags.Organization,
  InitiativeTags.Country,
];

export const initiativeTagsLabel = {
  [InitiativeTags.StaffOrganization]: 'Staff Organization - Only staff can add this organization to Portfolios, etc.',
  [InitiativeTags.VerifiedOrganization]: 'Verified Organization - Verified by WWG staff, similar to twitter verified blue tick. (Require Organization tag).',
  [InitiativeTags.Organization]: 'Organization - Root level reporting level, that allows you to create survey (main company)',
  [InitiativeTags.Country]: 'Country - Sub-organization tag (allow to create survey)',
}


export enum SurveyOverviewMode {
  Universal,
  Scope,
  ScopeGroups,
  SdgAndScope,
}


export enum ProductCodes {
  CompanyTrackerLight = 'company_tracker_light',
  CompanyTracker = 'company_tracker',
  PortfolioTracker = 'portfolio_tracker',
}

export interface AppConfig {
  code: string;
  productCode: ProductCodes;

  // App Setup
  name: string;
  logo: string;
  rootAppPath: string;

  /** Overall website change **/
  whitelabel?: {
    logo?: string;
  };
  /** App specific changes **/
  settings: {
    defaultSurveyOverviewMode?: SurveyOverviewMode;
    overviewRecommendedAddons: string[];
    settingsRecommendedAddons: string[];
    availableAddons?: string[];
    requiresTermsAndConditions?: boolean;
    agreementsRequired?: string[];
  };
}
